import { message } from 'antd'
import { wasRateLimitToastHandled } from './rateLimitToast'

const fieldLabel = (field, labels = {}) =>
  labels[field] ||
  field.replace(/_id$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

/**
 * Formats a Rails error hash (or other shapes the API emits) into a single
 * human-readable string.
 *
 * @param {Object|Array|string|null} errorData - Typically `error.response.data`
 * @param {Object} [options]
 * @param {Object} [options.labels] - Field name overrides, e.g. `{ user_id: 'Owner' }`
 * @returns {string} formatted message, or 'An error occurred' fallback
 */
export function formatApiError(errorData, options = {}) {
  if (!errorData) return 'An error occurred'
  if (typeof errorData === 'string') return errorData

  if (typeof errorData === 'object' && !Array.isArray(errorData)) {
    // Single-message shapes that the API uses for non-validation errors.
    if (typeof errorData.error === 'string') return errorData.error
    if (typeof errorData.message === 'string') return errorData.message

    // Rails errors hash: { name: ["is already taken"], ... }
    const errors = Object.entries(errorData)
      .map(([field, messages]) => {
        const messageArray = Array.isArray(messages) ? messages : [messages]
        const label = fieldLabel(field, options.labels)
        const combined = messageArray.filter(m => m != null && m !== '').join(', ')
        return combined ? `${label} ${combined}` : null
      })
      .filter(Boolean)

    if (errors.length) return errors.join('. ')
  }

  if (Array.isArray(errorData) && errorData.length) {
    return errorData.map(String).join(', ')
  }

  return 'An error occurred'
}

/**
 * Pulls the most user-actionable message out of an axios error.
 * Handles network errors, Rails error hashes, and status-code defaults.
 * Returns null when nothing recognizable is available, so callers can
 * decide on a fallback.
 *
 * @param {Error} error - Axios error
 * @param {Object} [options]
 * @param {Object} [options.labels] - Field name overrides for the errors hash
 * @returns {string|null}
 */
export function apiErrorMessage(error, options = {}) {
  // Network / no response — let the caller decide on a fallback.
  if (!error?.response) return null

  const { status, data } = error.response

  if (data) {
    const formatted = formatApiError(data, options)
    if (formatted && formatted !== 'An error occurred') return formatted
  }

  // Status-code fallbacks for shapes that don't carry a body.
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return "You don't have permission to do this."
  if (status === 404) return 'The item you tried to update no longer exists.'
  if (status >= 500) return 'Something went wrong on our end. Please try again.'

  return null
}

/**
 * Drop-in catch handler: shows a toast with the best-available message.
 *
 * Common usage:
 *   .catch(handleApiError)
 *   .catch(err => handleApiError(err, { prefix: collection.name }))
 *   .catch(err => handleApiError(err, { fallback: 'Could not save settings.' }))
 *
 * Always falls back to a generic message rather than leaving the user with
 * a silent failure.
 *
 * @param {Error} error - Axios error
 * @param {Object} [options]
 * @param {string} [options.prefix] - Prepended to the message: `${prefix}: ${detail}`
 * @param {string} [options.fallback] - Used when no message could be extracted (default: 'Something went wrong.')
 * @param {Object} [options.labels] - Field name overrides for Rails errors hashes
 * @param {Function} [options.onError] - Called with the original error after the toast
 */
export function handleApiError(error, options = {}) {
  const { prefix, fallback = 'Something went wrong.', labels, onError } = options

  if (wasRateLimitToastHandled(error)) {
    onError?.(error)
    return
  }

  const detail = apiErrorMessage(error, { labels }) || fallback
  message.error(prefix ? `${prefix}: ${detail}` : detail)
  onError?.(error)
}
